import { TBookingAddressForm, TBookingPackageForm } from 'types/form-types'

import {
  isGeneratedReturnStop,
  isReturnCollection,
  isReturnDelivery,
} from './waitAndReturn.helpers'

export type TPackageMapping = {
  collectionSequenceOrder: number
  deliverySequenceOrder: number
}

export type TRoutingStops = {
  collections: Array<TBookingAddressForm>
  deliveries: Array<TBookingAddressForm>
}

export type TUnrepresentedStop = {
  type: 'Collection' | 'Delivery'
  index: number
  address: TBookingAddressForm
}

// The sequence number held in a consignment's Collection or Delivery field, or null when blank.
const toSequence = (value: number | string | undefined | null): number | null => {
  if (value === '' || value === undefined || value === null) {
    return null
  }

  const sequence = Number(value)

  return Number.isNaN(sequence) ? null : sequence
}

// The user-created stops, which are the ones the collection-to-delivery pairing looks at.
export const getRoutingStops = (
  collectionAddresses: Array<TBookingAddressForm> | undefined,
  deliveryAddresses: Array<TBookingAddressForm> | undefined,
): TRoutingStops => ({
  collections: (collectionAddresses ?? []).filter((address) => !isGeneratedReturnStop(address)),
  deliveries: (deliveryAddresses ?? []).filter((address) => !isGeneratedReturnStop(address)),
})

// The return collection to return delivery pairing of a Wait & Return booking, or null until both
// generated stops exist.
export const getReturnMapping = (
  collectionAddresses: Array<TBookingAddressForm> | undefined,
  deliveryAddresses: Array<TBookingAddressForm> | undefined,
): TPackageMapping | null => {
  const returnCollection = (collectionAddresses ?? []).find(isReturnCollection)
  const returnDelivery = (deliveryAddresses ?? []).find(isReturnDelivery)

  if (!returnCollection || !returnDelivery) {
    return null
  }

  return {
    collectionSequenceOrder: returnCollection.sequenceOrder,
    deliverySequenceOrder: returnDelivery.sequenceOrder,
  }
}

// The collection-to-delivery pairings to pre-fill: one per delivery for a single collection, one
// per collection for a single delivery, none when both sides have several stops.
export const getAutoPackageMappings = (
  collections: Array<TBookingAddressForm>,
  deliveries: Array<TBookingAddressForm>,
): Array<TPackageMapping> => {
  if (!collections.length || !deliveries.length) {
    return []
  }

  if (collections.length === 1) {
    return deliveries.map((delivery) => ({
      collectionSequenceOrder: collections[0].sequenceOrder,
      deliverySequenceOrder: delivery.sequenceOrder,
    }))
  }

  if (deliveries.length === 1) {
    return collections.map((collection) => ({
      collectionSequenceOrder: collection.sequenceOrder,
      deliverySequenceOrder: deliveries[0].sequenceOrder,
    }))
  }

  return []
}

// A string key for a pairing, used to compare pairings.
export const getPackageMappingKey = (mapping: TPackageMapping) =>
  `${mapping.collectionSequenceOrder}-${mapping.deliverySequenceOrder}`

// Whether a consignment with this pairing already exists.
const hasMapping = (packages: Array<TBookingPackageForm>, mapping: TPackageMapping) =>
  packages.some(
    (pkg) =>
      toSequence(pkg.collectionSequenceOrder) === mapping.collectionSequenceOrder &&
      toSequence(pkg.deliverySequenceOrder) === mapping.deliverySequenceOrder,
  )

// The pairings that have no consignment yet.
export const getPackagesToAppend = (
  mappings: Array<TPackageMapping>,
  packages: Array<TBookingPackageForm>,
): Array<TPackageMapping> => mappings.filter((mapping) => !hasMapping(packages, mapping))

// Whether a consignment holds one of these pairings.
export const isOneOfPackageMappings = (
  pkg: TBookingPackageForm,
  mappings: Array<TPackageMapping>,
) => mappings.some((mapping) => hasMapping([pkg], mapping))

// Whether a consignment has neither a Collection nor a Delivery selected.
export const isBlankPackageMapping = (pkg: TBookingPackageForm) =>
  toSequence(pkg.collectionSequenceOrder) === null && toSequence(pkg.deliverySequenceOrder) === null

// Whether nothing has been entered about the goods of a consignment.
export const hasNoGoods = (pkg: TBookingPackageForm) =>
  [pkg.quantity, pkg.length, pkg.width, pkg.height, pkg.weight, pkg.reference, pkg.details].every(
    (value) => value === '' || value === undefined || value === null,
  )

// A consignment with its Collection and Delivery cleared.
export const clearPackageMapping = (pkg: TBookingPackageForm): TBookingPackageForm => ({
  ...pkg,
  collectionSequenceOrder: '',
  deliverySequenceOrder: '',
})

// The user-created stops that no consignment refers to.
export const getUnrepresentedStops = (
  collectionAddresses: Array<TBookingAddressForm> | undefined,
  deliveryAddresses: Array<TBookingAddressForm> | undefined,
  packages: Array<TBookingPackageForm> | undefined,
): Array<TUnrepresentedStop> => {
  const { collections, deliveries } = getRoutingStops(collectionAddresses, deliveryAddresses)
  const usedCollections = (packages ?? []).map((pkg) => toSequence(pkg.collectionSequenceOrder))
  const usedDeliveries = (packages ?? []).map((pkg) => toSequence(pkg.deliverySequenceOrder))

  const missingCollections: Array<TUnrepresentedStop> = collections
    .map((address, index) => ({ type: 'Collection' as const, index, address }))
    .filter((stop) => !usedCollections.includes(stop.address.sequenceOrder))

  const missingDeliveries: Array<TUnrepresentedStop> = deliveries
    .map((address, index) => ({ type: 'Delivery' as const, index, address }))
    .filter((stop) => !usedDeliveries.includes(stop.address.sequenceOrder))

  return [...missingCollections, ...missingDeliveries]
}

// Labels such as "Delivery 2 (Customer Harrogate)" for the stops with no consignment.
export const getUnrepresentedStopLabels = (
  stops: Array<TUnrepresentedStop>,
  isQuickQuote: boolean,
): Array<string> =>
  stops.map((stop) => {
    const position = `${stop.type} ${stop.index + 1}`
    const name = isQuickQuote ? stop.address.postcode : stop.address.name

    return name ? `${position} (${name})` : position
  })

// The label of a stop in the consignment dropdowns: its name (postcode on a quick quote), or its
// position, with "(return)" added for a generated stop.
export const getStopLabel = (
  address: TBookingAddressForm | undefined,
  type: 'Collection' | 'Delivery',
  index: number,
  isQuickQuote: boolean,
): string => {
  const preferred = isQuickQuote ? address?.postcode : address?.name
  const label = preferred || `${type} ${index + 1}`

  return isGeneratedReturnStop(address) ? `${label} (return)` : label
}

// The options of a consignment's Collection or Delivery dropdown.
export const getSequenceOrderOptions = (
  addresses: Array<TBookingAddressForm> | undefined,
  type: 'Collection' | 'Delivery',
  isQuickQuote: boolean,
) =>
  (addresses ?? []).map((address, index) => ({
    value: address.sequenceOrder,
    label: getStopLabel(address, type, index, isQuickQuote),
  }))

// Sequence number per stop, suffixed with the role of a generated return stop so it is told apart
// from a user stop with the same number.
export const toStopKeys = (addresses: Array<TBookingAddressForm> | undefined) =>
  (addresses ?? []).map(
    (address) =>
      `${address.sequenceOrder}${address.waitAndReturnRole ? `:${address.waitAndReturnRole}` : ''}`,
  )

// The sequence number part of a stop key.
export const stopKeyToSequence = (stopKey: string) => parseInt(stopKey, 10)

// The goods fields of a consignment, copied onto a new one.
export const copyPackageValues = (source: TBookingPackageForm | undefined) =>
  source
    ? {
        quantity: source.quantity,
        packageType: source.packageType,
        length: source.length,
        width: source.width,
        height: source.height,
        weight: source.weight,
      }
    : {}
